import * as React from 'react'
import { useEffect, useMemo } from 'react'
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Button, Field, makeStyles, tokens, Tooltip } from '@fluentui/react-components'
import { AddFilled, AddRegular, ArrowDownloadFilled, ArrowDownloadRegular, bundleIcon } from '@fluentui/react-icons'
import { useDispatch } from 'react-redux'
import useModules from '../../../hooks/module/useModules'
import useRoles, { Role } from '../../../hooks/useRoles'
import { AppAction } from '../../../config/appConfig'
import { MenuItemKey } from '../../../components/MainMenu'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import { ModuleType } from '../../../context/GlobalContext'
import AttributesList from '../../../components/Form/Attributes/AttributesList'
import { AttributeContextType, AttributeEntityDto, AttributeViewType, EntityAttributeType } from '../../../hooks/useAttributes'
import { useFormikContext } from 'formik'
import useAxios from '../../../hooks/useAxios'
import useSubmitDocumentGenerationRequest from '../../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import { DocumentGenerationType } from '../../../hooks/document/useDocumentGenerationHandler'
import { SortDirection } from '../../../config/settingConfig'
import { AxiosResponse } from 'axios'
import BaseOverview, {
    BaseOverviewCount,
    BaseOverviewHeaderItemProps,
    BaseOverviewLineItemProps,
    parseFilterValuesToParams,
    useOverviewStyles,
} from '../../BaseOverview/BaseOverview'
import { BaseOverviewFilterType } from '../../BaseOverview/BaseOverviewFilter'
import { BaseOverviewSortingType } from '../../BaseOverview/BaseSortMenu'
import { InvoiceOverviewDto, InvoiceStatus, InvoiceType } from '../../../config/finance/invoiceConfig'
import DateRange from '../../../components/DatePicker/DateRange'
import FilePicker from '../../../components/TagPicker/FilePicker'
import InvoiceStatusPicker from '../../../components/TagPicker/InvoiceStatusPicker'
import BooleanCombobox from '../../../components/Combobox/BooleanCombobox'
import PartyCombobox from '../../../components/Combobox/PartyCombobox'
import { DateTime } from 'luxon'
import { parseDate } from '../../../helpers/dateHelper'
import UserPicker from '../../../components/TagPicker/UserPicker'
import InvoiceListItem from './InvoiceListItem'
import InvoiceHeaderItem from './InvoiceHeaderItem'
import { formatNumber, parseNumber } from '../../../helpers/numberHelper'
import { PricePrecedence } from '../../../config/finance/financeConfig'
import { FileAction } from '../../../config/fileConfig'

export const useItemStyles = makeStyles({
    listInvoiceItem: {
        gridTemplateColumns:
            'var(--baseOverviewAvatarWidth) var(--baseOverviewIconWidth) var(--baseOverviewIconWidth) var(--baseOverviewFileWidth) var(--baseOverviewDateWidth) auto 100px 100px 100px var(--baseOverviewFileWidth) var(--baseOverviewIconWidth)',

        '& button[aria-label="hover"]': {
            display: 'none',
            marginLeft: tokens.spacingVerticalXS,
            height: '24px',
        },

        '&:hover': {
            '& a, & a span': {
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
            },

            '& button[aria-label="hover"]': {
                display: 'inline-grid',
            },
        },
    },
    listInvoiceItemMedium: {
        gridTemplateColumns:
            'var(--baseOverviewAvatarWidth) var(--baseOverviewIconWidth) var(--baseOverviewIconWidth) var(--baseOverviewFileWidth) var(--baseOverviewDateWidth) auto var(--baseOverviewPriceWidth) var(--baseOverviewPriceWidth) var(--baseOverviewPriceWidth) var(--baseOverviewFileWidth) var(--baseOverviewIconWidth)',
    },
    listInvoiceItemSmall: {
        gridTemplateColumns: 'var(--baseOverviewIconWidth) auto',
        gridTemplateRows: 'auto 1fr',
    },
    listInvoiceItemTiny: {
        gridTemplateColumns: 'var(--baseOverviewIconWidth) auto',
        gridTemplateRows: 'auto 1fr',
    },
    listInvoiceItemSmallBottomRow: {
        display: 'grid',
        gridTemplateColumns:
            'var(--baseOverviewIconWidth) var(--baseOverviewDateWidth) auto var(--baseOverviewPriceWidth) var(--baseOverviewIconWidth)',
    },
    listInvoiceItemTinyBottomRow: {
        display: 'grid',
        gridTemplateColumns: 'var(--baseOverviewDateWidth) auto var(--baseOverviewPriceWidth)',
        gap: tokens.spacingVerticalS,
    },
    listInvoiceItemTitle: {
        display: 'grid',
        gridTemplateColumns: '1fr var(--baseOverviewFileWidth) auto',
    },
    listInvoiceItemPaidIcon: {
        height: '24px',
        width: '14px',
    },
    turnoversContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
    },
    turnoverContainer: {
        display: 'grid',
        gridTemplateColumns: 'auto 24px 80fr',
        columnGap: tokens.spacingVerticalS,
    },
    buttons: {
        display: 'flex',
        flexWRap: 'nowrap',
    },
})

const AddIcon = bundleIcon(AddFilled, AddRegular)
const DownloadIcon = bundleIcon(ArrowDownloadFilled, ArrowDownloadRegular)

// Base item for the turnover component
export type BaseInvoiceTurnoverDetails = {
    currencies: InvoiceCurrencyOverviewCount[]
}

export type ExtendedInvoiceOverviewDto = InvoiceOverviewDto &
    BaseInvoiceTurnoverDetails & {
        // Is calculated during runtime and not actually returned by the API.
        dueDate: Date
        isDue: boolean
    }

export type InvoiceLineItemProps = BaseOverviewLineItemProps<ExtendedInvoiceOverviewDto, InvoiceFilterType, InvoiceSortingType>

export type InvoiceHeaderItemProps = BaseOverviewHeaderItemProps<
    ExtendedInvoiceOverviewDto,
    InvoiceFilterType,
    InvoiceSortingType,
    InvoiceOverviewCount
>

export type InvoiceOverviewCount = BaseOverviewCount & BaseInvoiceTurnoverDetails

export type InvoiceCurrencyOverviewCount = {
    type: InvoiceType
    count: number
    currency: string
    outstandingTotal: string
    unsettledTotal?: string
    subtotal: string
    vat: string
    total: string

    paidPrice?: string
    isPartiallyPaid: boolean
    isPaid: boolean
}

export type InvoiceFilterType = AttributeEntityDto &
    BaseOverviewFilterType & {
        from?: string
        to?: string
        files?: string[]
        fileOwners?: string[]
        crmRelations?: string[]
        statuses?: InvoiceStatus[]
        isSettled?: boolean
        hasTransaction?: boolean
    }

type InvoiceSorting = {
    id: string
    number: string
    date: string
    'recipient.name': string
    'outstandingPrice.total': string
    'files.reference': string
}

export type InvoiceSortingType = BaseOverviewSortingType<InvoiceSorting>

export function InvoiceFilter() {
    const { values, setFieldValue } = useFormikContext<InvoiceFilterType>()
    const modules = useModules()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const orientation = useMemo(() => (isViewport('<', Viewport.Large) ? 'vertical' : 'horizontal'), [viewport])

    const updateFieldValue = (field: string, value: any, shouldValidate?: boolean) => {
        setFieldValue('tag', undefined, false).then(() => setFieldValue(field, value, shouldValidate))
    }

    return (
        <>
            <DateRange
                orientation={orientation}
                startValue={values.from}
                endValue={values.to}
                changeStartDate={date => updateFieldValue('from', date)}
                changeEndDate={date => updateFieldValue('to', date)}
                showLabel
            />
            <Field label={translate('invoice.filter.file')} orientation={orientation}>
                <FilePicker
                    selectedOptions={values.files || []}
                    onOptionsSelect={(_, selectedOptions) => updateFieldValue('files', selectedOptions)}
                />
            </Field>
            <Field label={translate('invoice.filter.owner')} orientation={orientation}>
                <UserPicker
                    selectedOptions={values.fileOwners || []}
                    onOptionsSelect={(_, selectedOptions) => updateFieldValue('fileOwners', selectedOptions)}
                />
            </Field>
            <Field label={translate('base.filter.crmrelation')} orientation={orientation}>
                <PartyCombobox
                    selectedOptions={values.crmRelations || []}
                    onOptionSelect={(_, data) => updateFieldValue('crmRelations', data.optionValue ? [data.optionValue] : [])}
                    params={{ crmRelation: true }}
                />
            </Field>
            <Field label={translate('invoice.status.label')} orientation={orientation}>
                <InvoiceStatusPicker
                    selectedOptions={values.statuses || []}
                    onOptionsSelect={(_, selectedOptions: InvoiceStatus[]) => updateFieldValue('statuses', selectedOptions)}
                />
            </Field>
            <Field label={translate('invoice.filter.issettled')} orientation={orientation}>
                <BooleanCombobox
                    required
                    selectedOptions={[values.isSettled?.toString() || 'mixed']}
                    onOptionSelect={(_, data) => updateFieldValue('isSettled', data.checked === 'mixed' ? undefined : data.checked)}
                />
            </Field>
            {modules.has(ModuleType.Accounting) && (
                <Field label={translate('invoice.filter.hastransaction')} orientation={orientation}>
                    <BooleanCombobox
                        required
                        selectedOptions={[values.hasTransaction?.toString() || 'mixed']}
                        onOptionSelect={(_, data) => updateFieldValue('hasTransaction', data.checked === 'mixed' ? undefined : data.checked)}
                    />
                </Field>
            )}
            {modules.has(ModuleType.Attributes) && (
                <Accordion collapsible>
                    <AccordionItem value="attributes">
                        <AccordionHeader>{translate('invoice.filter.attributes')}</AccordionHeader>
                        <AccordionPanel>
                            <AttributesList
                                orientation={orientation}
                                entityType={EntityAttributeType.File}
                                context={[AttributeContextType.File]}
                                viewType={AttributeViewType.Filter}
                                entity={values}
                                setFieldValue={(field, value) => setFieldValue(values[field], value)}
                            />
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            )}
        </>
    )
}

const headerSortingItems: InvoiceSortingType[] = [
    {
        property: ['number'],
        direction: SortDirection.Ascending,
        descriptionKey: 'sorting.numberasc',
    },
    {
        property: ['number'],
        direction: SortDirection.Descending,
        default: true,
        descriptionKey: 'sorting.numberdesc',
    },
    {
        property: ['date', 'number'],
        direction: SortDirection.Ascending,
        descriptionKey: 'sorting.dateasc',
    },
    {
        property: ['date', 'number'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.datedesc',
    },
    {
        property: ['recipient.name', 'number'],
        direction: SortDirection.Ascending,
        descriptionKey: 'sorting.az',
    },
    {
        property: ['recipient.name', 'number'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.za',
    },
    {
        property: ['outstandingPrice.total', 'number'],
        direction: SortDirection.Ascending,
        descriptionKey: 'sorting.priceasc',
    },
    {
        property: ['outstandingPrice.total', 'number'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.pricedesc',
    },
    {
        property: ['files.reference', 'number'],
        direction: SortDirection.Ascending,
        descriptionKey: 'sorting.referenceasc',
    },
    {
        property: ['files.reference', 'number'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.referencedesc',
    },
]

export function HeaderActionsButtons() {
    const history = useHistory()
    const roles = useRoles()
    const { t: translate } = useTranslation()
    const { values } = useFormikContext<InvoiceFilterType>()

    const submitReportRequest = useSubmitDocumentGenerationRequest<InvoiceFilterType>(DocumentGenerationType.InvoiceXls)

    return (
        <>
            <Tooltip withArrow relationship={'label'} content={translate('action.create.invoice.title')}>
                <Button icon={<AddIcon />} appearance={'primary'} onClick={() => history.push('/invoice/add')} />
            </Tooltip>
            {roles.all([Role.Report]) && (
                <Tooltip withArrow relationship={'label'} content={translate('action.download.selection')}>
                    <Button icon={<DownloadIcon />} appearance={'secondary'} onClick={() => submitReportRequest(parseFilterValuesToParams(values))} />
                </Tooltip>
            )}
        </>
    )
}

export default function InvoiceOverview() {
    const axios = useAxios()
    const dispatch = useDispatch()
    const location = useLocation()
    const overviewStyles = useOverviewStyles()
    const itemStyles = useItemStyles()
    const { viewport, isViewport } = useViewport()

    const listItemTiny = useMemo<boolean>(() => isViewport('<', Viewport.OutlookNew), [viewport])
    const listItemSmall = useMemo<boolean>(() => isViewport('<', Viewport.XL), [viewport])
    const listItemMedium = useMemo<boolean>(() => isViewport('<', Viewport.XXL), [viewport])

    const pageParamsValues = useMemo<InvoiceFilterType>(() => {
        const params = new URLSearchParams(location.search)
        const crmRelations = params.get('crmRelations')?.split(',').filter(Boolean)
        const statuses = params.get('statuses')?.split(',').filter(Boolean) as InvoiceStatus[]
        if (!crmRelations || !statuses) {
            return undefined
        }

        return {
            crmRelations: crmRelations ?? [],
            statuses: statuses ?? [],
        }
    }, [])

    //Reset breadcrumbs and data
    useEffect(() => {
        dispatch({ type: AppAction.SetBreadcrumbs, payload: [MenuItemKey.Invoice] })
        dispatch({ type: FileAction.ResetCurrent })
    }, [])

    const defaultFilterValues = useMemo<InvoiceFilterType>(
        () => ({
            from: DateTime.local().startOf('year').minus({ year: 3 }).toISODate(),
            to: undefined,
            statuses: [InvoiceStatus.Draft, InvoiceStatus.Final, InvoiceStatus.Settled, InvoiceStatus.Credited],
            isSettled: null,
            hasTransaction: null,
            attributes: [],
            query: '',
            ...pageParamsValues,
        }),
        [],
    )

    const countItems = (_column: number, filterValues?: InvoiceFilterType): Promise<InvoiceOverviewCount> => {
        return axios
            .get<InvoiceOverviewCount>('/finance/invoice/count', { params: parseFilterValuesToParams(filterValues) })
            .then((response: AxiosResponse<InvoiceOverviewCount>): InvoiceOverviewCount => {
                return {
                    ...response.data,
                    currencies: response.data.currencies.map(currency => {
                        return {
                            ...currency,
                            type: InvoiceType.Regular,
                            vat: (parseNumber(currency.total, 'en') - (parseNumber(currency.subtotal, 'en') || 0)).toString(),
                            price: {
                                precedence: PricePrecedence.Total,
                                currency: currency.currency,
                                vat: [
                                    {
                                        id: '0',
                                        type: 'NL_NORMAL',
                                        value: (parseNumber(currency.total, 'en') - (parseNumber(currency.subtotal, 'en') || 0)).toString(),
                                    },
                                ],
                                subtotal: currency.subtotal,
                                total: currency.total,
                            },
                            outstandingPrice: {
                                precedence: PricePrecedence.Total,
                                currency: currency.currency,
                                vat: [
                                    {
                                        id: '0',
                                        type: 'NL_NORMAL',
                                        value: (parseNumber(currency.total, 'en') - (parseNumber(currency.subtotal, 'en') || 0)).toString(),
                                    },
                                ],
                                subtotal: undefined,
                                total: currency.outstandingTotal,
                            },
                            paidPrice: (parseNumber(currency.total, 'en') - (parseNumber(currency.outstandingTotal, 'en') || 0)).toString(),
                            unsettledPrice: undefined,
                            isPartiallyPaid: false,
                            isPaid: false,
                        }
                    }),
                }
            })
    }

    const loadItems = (
        _column: number,
        filterValues?: InvoiceFilterType,
        sortingValues?: InvoiceSortingType,
        start?: number,
        size?: number,
    ): Promise<ExtendedInvoiceOverviewDto[]> => {
        return axios
            .get<InvoiceOverviewDto[]>('/finance/invoice', {
                params: { ...parseFilterValuesToParams(filterValues, sortingValues, start, size) },
            })
            .then((response: AxiosResponse<InvoiceOverviewDto[]>): ExtendedInvoiceOverviewDto[] =>
                (response?.data || []).map((item: InvoiceOverviewDto) => {
                    const dueDate = parseDate(item.date).plus({ days: item.term || 0 })
                    const diffDate = dueDate.diff(DateTime.now().startOf('day')).as('days')
                    const extendedItem: ExtendedInvoiceOverviewDto = {
                        ...item,
                        dueDate: dueDate.toBSON(),
                        isDue: diffDate <= 0,
                        currencies: [
                            {
                                count: 1,
                                currency: item.price.currency,
                                type: item.type,
                                outstandingTotal: item.outstandingPrice?.total,
                                unsettledTotal: item.unsettledPrice?.total,
                                subtotal: item.price.subtotal,
                                vat: formatNumber(
                                    item.price.vat?.map(vat => parseNumber(vat.value, 'en')).reduce((a, b) => a + b, 0.0),
                                    '0.00',
                                    'en',
                                ),
                                total: item.price.total,
                                paidPrice: (parseNumber(item.price.total, 'en') - (parseNumber(item.outstandingPrice?.total, 'en') || 0)).toString(),
                                isPartiallyPaid: false,
                                isPaid: false,
                            },
                        ],
                    }

                    return {
                        ...extendedItem,
                        currencies: [
                            {
                                ...extendedItem.currencies[0],
                                isPartiallyPaid:
                                    parseNumber(extendedItem.currencies[0].paidPrice, 'en') > 0 &&
                                    parseNumber(extendedItem.currencies[0].paidPrice, 'en') < parseNumber(extendedItem.price.total, 'en'),
                                isPaid: (parseNumber(item.outstandingPrice?.total, 'en') || 1) === 0,
                            },
                        ],
                    }
                }),
            )
    }

    return (
        <BaseOverview<ExtendedInvoiceOverviewDto, InvoiceFilterType, InvoiceSortingType, InvoiceOverviewCount>
            defaultFilterValues={defaultFilterValues}
            countItems={countItems}
            loadItems={loadItems}
            listColumnFullWidth
            listItemHeaderContent={InvoiceHeaderItem}
            listItemContent={props => <InvoiceListItem {...props} />}
            listItemClassnames={props => {
                return [
                    itemStyles.listInvoiceItem,
                    listItemMedium && itemStyles.listInvoiceItemMedium,
                    listItemSmall && itemStyles.listInvoiceItemSmall,
                    listItemTiny && itemStyles.listInvoiceItemTiny,
                    props.item &&
                        props.item.isDue &&
                        ![InvoiceStatus.Done, InvoiceStatus.CreditedDone, InvoiceStatus.SettledDone].includes(props.item.status) &&
                        overviewStyles.colorRed,
                ]
            }}
            headerActionsButtons={HeaderActionsButtons}
            headerSortingItems={headerSortingItems}
            filterContent={InvoiceFilter}
            inputPlaceholderKey={'invoice.filter.search'}
            localStorageKeyPrefix={pageParamsValues ? undefined : 'HammockInvoiceOverview'}
        />
    )
}
